import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth } from '../contexts/AuthContext'

const PaywallScreen = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [step, setStep] = useState('pricing') // 'pricing', 'account', 'payment'

  useEffect(() => {
    // Load Stripe buy button script
    const script = document.createElement('script')
    script.src = 'https://js.stripe.com/v3/buy-button.js'
    script.async = true
    document.body.appendChild(script)

    return () => {
      // Cleanup script on component unmount
      if (document.body.contains(script)) {
        document.body.removeChild(script)
      }
    }
  }, [])

  const features = [
    { icon: '🎯', title: '8+ Actions Per Scenario', description: 'Go beyond the basic 3 - get comprehensive guidance for every situation' },
    { icon: '💬', title: 'Smart Chat Coaching', description: 'Tell the app "this doesn\'t fit my life" and get personalized alternatives' },
    { icon: '📅', title: 'Cycle Calendar & Planning', description: 'Visual calendar with your cycle, moon phases, and strategic planning tools' },
    { icon: '📊', title: 'Personal Insights Dashboard', description: 'Track patterns, energy levels, and optimize your monthly rhythms' },
    { icon: '🔐', title: 'Secure Account & Sync', description: 'Your data is safely stored in the cloud and synced across all devices' },
    { icon: '🔔', title: 'Smart Notifications', description: 'Get notified about phase transitions and optimal timing for activities' },
    { icon: '🌟', title: 'All Life Scenarios', description: 'Complete guidance for every situation: travel, relationships, health, finances' }
  ]

  // Payment plan from Stripe
  const paymentPlan = {
    name: "Cycle CEO",
    amount: 5,
    priceId: "price_1SA9tNERO7JYErJ6Dy2NJ2XY",
    paymentLink: "https://buy.stripe.com/00wbJ18S06EwbMR3Fwgw005",
    currency: "gbp"
  }

  const handleGetPremium = () => {
    if (user) {
      // User has account, go directly to payment
      handlePayment()
    } else {
      // User needs account setup first
      navigate('/account-setup?premium=true')
    }
  }

  const handlePayment = () => {
    // Open Stripe payment link in new tab
    window.open(paymentPlan.paymentLink, '_blank')
  }

  const handleCreateAccountFirst = () => {
    navigate('/account-setup?premium=true')
  }

  return (
    <div className="min-h-screen bg-white px-6 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="max-w-md mx-auto"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <button
            onClick={() => navigate('/')}
            className="text-sm text-gray-500 mb-6 hover:text-black transition-colors"
          >
            ← Back
          </button>
          <h1 className="italiana-font text-3xl text-black mb-8 leading-tight">
            Turn Your Natural Rhythm Into Your Biggest Business Advantage 🌙
          </h1>
        </div>

        {/* Problem Hook */}
        <div className="mb-8">
          <div className="bg-gray-50 p-6 rounded-lg mb-6">
            <p className="text-sm italic text-gray-600 text-center mb-4">
              "I love the 3 actions, but I need more depth..."
            </p>
            <p className="text-sm italic text-gray-600 text-center">
              "What if this doesn't fit my exact situation today?"
            </p>
          </div>
          <p className="text-sm leading-relaxed text-gray-800 text-center">
            You've experienced how powerful it is to work WITH your natural rhythm instead of against it. 
            Now unlock the complete system that adapts to your real life, every day.
          </p>
        </div>

        {/* Pricing Card */}
        <div className="mb-8">
          <div className="bg-white border-2 border-gold rounded-lg p-6 shadow-lg">
            <div className="text-center mb-6">
              <h3 className="italiana-font text-2xl text-black mb-2">{paymentPlan.name}</h3>
              <div className="flex items-center justify-center mb-4">
                <span className="text-3xl font-bold text-gold">£{paymentPlan.amount}</span>
                <span className="text-gray-600 ml-2">/month</span>
              </div>
              <p className="text-sm text-gray-600">Complete cycle guidance system</p>
            </div>

            {/* Features */}
            <div className="space-y-3 mb-6">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex items-start space-x-3"
                >
                  <span className="text-lg flex-shrink-0">{feature.icon}</span>
                  <div>
                    <h4 className="font-semibold text-black text-sm mb-1">
                      {feature.title}
                    </h4>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Payment Options */}
            <div className="space-y-3">
              {/* Main CTA - Direct Payment */}
              <motion.button
                onClick={handleGetPremium}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full gold-bg text-white py-4 px-6 rounded-lg font-medium hover:opacity-90 transition-opacity"
              >
                {user ? 'Get Premium Access →' : 'Get Premium + Create Account →'}
              </motion.button>

              {/* Alternative - Account Setup First (only show if no user) */}
              {!user && (
                <motion.button
                  onClick={handleCreateAccountFirst}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full border border-gold text-gold py-3 px-6 rounded-lg font-medium hover:bg-gold hover:text-white transition-all"
                >
                  Set Up Account First →
                </motion.button>
              )}
            </div>
          </div>
        </div>

        {/* Account Benefits (if no user) */}
        {!user && (
          <div className="mb-8">
            <div className="bg-gold bg-opacity-10 p-6 rounded-lg">
              <h3 className="font-semibold text-black mb-3 text-center">
                ✨ Why Create Your Account?
              </h3>
              <div className="space-y-2 text-sm text-gray-700">
                <div className="flex items-start space-x-2">
                  <span className="gold-accent">•</span>
                  <span>Secure payment tracking and premium access management</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="gold-accent">•</span>
                  <span>Your cycle data is safely stored and synced across devices</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="gold-accent">•</span>
                  <span>Never lose your premium features or personalized settings</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="gold-accent">•</span>
                  <span>Get notifications for optimal timing and phase transitions</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Social Proof */}
        <div className="mb-8">
          <div className="bg-gold bg-opacity-10 p-6 rounded-lg">
            <p className="text-sm text-gray-700 text-center mb-3">
              "Finally, a system that works with my body instead of against it. I've 2x'd my revenue by timing my launches perfectly."
            </p>
            <p className="text-xs text-gray-500 text-center">
              — Sarah K., Online Business Owner
            </p>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="text-center">
          <p className="text-xs text-gray-500 mb-2">
            🔒 Secure checkout • Cancel anytime • 30-day money-back guarantee
          </p>
          <p className="text-xs text-gray-500">
            Join 2,847+ women using their cycles as their secret weapon
          </p>
        </div>
      </motion.div>
    </div>
  )
}

export default PaywallScreen